/**
 * Resolve the bubble diagram for a workshop: override (effective) wins,
 * else the authored Mission Control / HAW layout.
 */

import type { BubbleConfig } from "@/components/diagrams/types";
import {
  WORKSHOP_PANEL_DATA,
  type WorkshopPanelData,
} from "@/features/missionControl/workshop-panel-data";
import {
  bubbleConfigsFromDiagram,
  type DiagramOverride,
} from "@/features/workshop/diagram-presets";
import { resolveDiagram } from "@/features/workshop/overrides/workshop-override-content";
import type { WorkshopOverride } from "@/features/workshop/overrides/workshop-override-content";
import { HAW_MODES, type HawMode } from "@/features/workshop/haw-content";

export type ResolvedWorkshopDiagram = {
  viewBox: string;
  connectors: Array<[number, number, number, number]>;
  bubbles: BubbleConfig[];
  /** True when drawing from a facilitator diagram override. */
  fromOverride: boolean;
  diagram?: DiagramOverride;
};

export function resolveWorkshopDiagram(
  workshopId: WorkshopPanelData["workshopId"],
  override: WorkshopOverride | undefined,
): ResolvedWorkshopDiagram {
  const diagram = resolveDiagram(override);
  if (diagram) {
    const built = bubbleConfigsFromDiagram(diagram, workshopId);
    return { ...built, fromOverride: true, diagram };
  }
  const data = WORKSHOP_PANEL_DATA[workshopId];
  return {
    viewBox: data.viewBox,
    connectors: data.connectors,
    bubbles: data.bubbles,
    fromOverride: false,
  };
}

/** HAW modes for WS4 — from diagram override when present, else authored. */
export function resolveHawModes(
  override: WorkshopOverride | undefined,
): HawMode[] {
  const diagram = resolveDiagram(override);
  if (!diagram || diagram.bubbles.length === 0) return HAW_MODES;

  const layoutHasCentre =
    diagram.bubbleCount === 5 || diagram.bubbleCount === 7;
  return diagram.bubbles.map((b, i) => {
    const isCentre = layoutHasCentre && i === diagram.bubbles.length - 1;
    const fallback = HAW_MODES[Math.min(i, HAW_MODES.length - 1)]!;
    const id = isCentre
      ? ("centre" as const)
      : ((["tl", "tr", "bl", "br"] as const)[i % 4] ?? "tl");
    return {
      id,
      title: b.tipTitle || b.titleLines.join(" ") || fallback.title,
      subtitle: b.subtitle ?? fallback.subtitle,
      badge: b.badge ?? (isCentre ? undefined : fallback.badge),
      body: b.tipBody || b.bodyLines.join(" ") || fallback.body,
      accent: fallback.accent,
      lucide: b.lucide ?? fallback.lucide,
    } satisfies HawMode;
  });
}
